import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Eye } from "lucide-react";
import { BrandButton } from "@/components/PageHeader";
import { BasicsSection } from "@/components/event-setup/BasicsSection";
import { CategoriesSection } from "@/components/event-setup/CategoriesSection";
import { PaymentAlertSection, DEFAULT_ALERT } from "@/components/event-setup/PaymentAlertSection";
import { BannersSection, PRESETS } from "@/components/event-setup/BannersSection";
import { AccommodationSection } from "@/components/event-setup/AccommodationSection";
import { SouvenirDinnerSection } from "@/components/event-setup/ExtrasSections";
import { SponsorsSection } from "@/components/event-setup/SponsorsSection";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { useUnsavedGuard } from "@/components/event-setup/useUnsavedGuard";

const blank = () => ({
  title: "", theme: "", date: "", start_date: "", registration_deadline: "", venue: "", region: "Greater Accra", status: "Registration Open",
  cpd_points: 0, capacity: 0, featured: false, image: PRESETS[0], banners: [PRESETS[0]],
  souvenir_dinner: { enabled: false, description: "", options: [] },
  categories: [],
  protocol: { enabled: false, subcategories: [] },
  sponsor_category: { enabled: false },
  online_attendance: false,
  good_standing_category: { enabled: true, fee: 0 },
  not_good_standing_category: { enabled: true, fee: 0 },
  non_member_category: { enabled: false, fee: 0 },
  student_category: { enabled: false, fee: 0 },
  payment_alert: DEFAULT_ALERT,
  accommodation_mode: "display", hotels: [], sponsors: [],
});

const fromEvent = (e) => ({ ...blank(), ...e, souvenir_dinner: e.souvenir_dinner || blank().souvenir_dinner, protocol: e.protocol || blank().protocol, sponsor_category: e.sponsor_category || { enabled: false }, online_attendance: !!e.online_attendance, good_standing_category: e.good_standing_category || { enabled: false, fee: 0 },
  not_good_standing_category: e.not_good_standing_category || { enabled: false, fee: 0 }, non_member_category: e.non_member_category || { enabled: false, fee: 0 }, student_category: e.student_category || { enabled: false, fee: 0 },
  banners: (e.banners || []).includes(e.image) ? e.banners : [e.image, ...(e.banners || [])],
  payment_alert: Object.fromEntries(Object.entries(DEFAULT_ALERT).map(([k, v]) => [k, e.payment_alert?.[k] || v])) });

const problems = (f) => {
  if (f.title.trim().length < 3) return "Add an event title (3+ characters)";
  if (f.date.trim().length < 3) return "Add the event date(s)";
  if (f.venue.trim().length < 2) return "Add the venue";
  if (f.categories.some((c) => !c.name.trim())) return "Every older custom category needs a name";
  if (![f.good_standing_category, f.not_good_standing_category, f.non_member_category, f.student_category, f.sponsor_category].some((c) => c?.enabled) && !(f.protocol.enabled && f.protocol.subcategories.length)) return "Switch on at least one registration category";
  if (!f.good_standing_category?.enabled && !f.not_good_standing_category?.enabled && !f.sponsor_category?.enabled && !f.protocol.enabled) return "Switch on a category members can choose: Member in Good Standing, Member Not in Good Standing, Protocol or Sponsor";
  if (f.protocol.enabled && f.protocol.subcategories.some((s) => !s.name.trim())) return "Every Protocol subcategory needs a name";
  if (f.souvenir_dinner.enabled && f.souvenir_dinner.options.some((o) => !o.name.trim())) return "Every Souvenir & Dinner category needs a name";
  if (f.hotels.some((h) => !h.name.trim())) return "Every accommodation needs a name";
  if (f.hotels.some((h) => h.rooms.some((r) => !r.name.trim()))) return "Every room type needs a name";
  return null;
};

const NAV = [["details", "Event details"], ["categories", "Categories & fees"], ["souvenir-dinner", "Souvenir & Dinner"], ["banners", "Banner images"], ["accommodation", "Accommodation"], ["sponsors", "Event sponsors"], ["payment-alerts", "Payment alerts"]];

export default function EventSetup() {
  const { id } = useParams();
  const nav = useNavigate();
  const { refresh } = useApp();
  const [f, setF] = useState(id ? null : blank());
  const [saved, setSaved] = useState(() => (id ? null : JSON.stringify(f)));
  const [published, setPublished] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedEvent, setSavedEvent] = useState(null);
  const set = (patch) => setF((s) => ({ ...s, ...patch }));
  const load = (ev) => { const x = fromEvent(ev); setSavedEvent(ev); setF(x); setSaved(JSON.stringify(x)); setPublished(ev.published); };
  const dirty = !!f && saved !== null && JSON.stringify(f) !== saved;
  const dialog = useUnsavedGuard(dirty);

  useEffect(() => {
    if (id) api.get(`/events/${id}`).then((r) => {
      if (r.data.deleted) { toast.error("Undelete this event in Event Manager Studio before editing"); nav("/manage-events"); }
      else load(r.data);
    }).catch(() => nav("/manage-events"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, nav]);

  const save = async (publish) => {
    const issue = problems(f);
    if (issue) return toast.error(issue);
    setBusy(true);
    try {
      const r = id ? await api.put(`/events/${id}`, f) : await api.post("/events", f);
      if (publish && !published) await api.patch(`/events/${r.data.id}/publish`, { published: true });
      toast.success(`${publish ? "Event saved and published" : id ? "Event saved" : "Draft event created"}${r.data.bills_recalculated ? ` · ${r.data.bills_recalculated} registration bills recalculated` : ""}${r.data.registrations_renamed ? ` · Renamed on ${r.data.registrations_renamed} existing registration${r.data.registrations_renamed === 1 ? "" : "s"}` : ""}`);
      refresh();
      load((await api.get(`/events/${r.data.id}`)).data);
      if (!id) setTimeout(() => nav(`/manage-events/${r.data.id}`, { replace: true }), 0);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  if (!f) return <div className="text-gray-500" data-testid="setup-loading">Loading event…</div>;
  return (
    <div data-testid="event-setup-page">
      <div className="sticky top-16 z-20 -mx-4 sm:-mx-8 px-4 sm:px-8 py-3 mb-6 bg-[#F8F9FA]/90 backdrop-blur-md border-b border-[#E5E7EB] flex flex-wrap items-center gap-3">
        <button onClick={() => nav("/manage-events")} className="inline-flex items-center gap-1.5 text-sm font-semibold hover:text-[#E60000]" data-testid="setup-back-button"><ArrowLeft className="w-4 h-4" />Events</button>
        <h1 className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight truncate" data-testid="page-title">{id ? `Edit: ${f.title || "Event"}` : "New event setup"}</h1>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${published ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-600 border-gray-200"}`}>{published ? "Published" : "Draft"}</span>
        {dirty && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200" data-testid="setup-unsaved-badge">Unsaved changes</span>}
        {dialog}
        <div className="ml-auto flex gap-2">
          <BrandButton variant="outline" onClick={() => save(false)} disabled={busy} data-testid="setup-save-button"><Save className="w-4 h-4" />{published ? "Save changes" : "Save draft"}</BrandButton>
          {!published && <BrandButton onClick={() => save(true)} disabled={busy} data-testid="setup-save-publish-button"><Eye className="w-4 h-4" />Save & publish</BrandButton>}
        </div>
      </div>
      {id && <div className="rounded-md border border-amber-200 bg-amber-50 p-3 mb-5 text-sm text-amber-900" data-testid="setup-repricing-notice">Saving changed fees automatically recalculates all existing registration bills, including fully paid registrations. Recorded payments stay unchanged; any overpayment is shown for Accounts to review.</div>}
      <div className="grid grid-cols-1 xl:grid-cols-[200px_1fr] gap-6 items-start">
        <nav className="hidden xl:block sticky top-36 space-y-1" data-testid="setup-section-nav">
          {NAV.map(([k, l], i) => (
            <a key={k} href={`#${k}`} className="flex gap-2 text-sm px-3 py-2 rounded-md text-gray-600 hover:bg-white hover:text-[#111] transition-colors" data-testid={`setup-nav-${k}`}>
              <span className="font-mono text-xs text-[#E60000]">0{i + 1}</span>{l}
            </a>
          ))}
        </nav>
        <div className="space-y-6 min-w-0 max-w-4xl">
          <BasicsSection f={f} set={set} />
          <CategoriesSection f={f} set={set} move={id && { eventId: id, savedEvent, dirty, onMoved: async () => load((await api.get(`/events/${id}`)).data) }} />
          <SouvenirDinnerSection value={f.souvenir_dinner} onChange={(souvenir_dinner) => set({ souvenir_dinner })} />
          <BannersSection image={f.image} banners={f.banners} set={set} />
          <AccommodationSection mode={f.accommodation_mode} hotels={f.hotels} set={set} />
          <SponsorsSection sponsors={f.sponsors} onChange={(sponsors) => set({ sponsors })} />
          <PaymentAlertSection value={f.payment_alert} onChange={(payment_alert) => set({ payment_alert })} />
        </div>
      </div>
    </div>
  );
}
